/**
 * Tipos e chamadas de "Dados da empresa".
 *
 * O contrato espelha `api/internal/modulos/conta/empresa.go`. Só IDENTIDADE:
 * o que sai nos documentos. Regime tributário, alíquota, série e certificado
 * não moram aqui — são parâmetro fiscal, são outra tela.
 *
 * Todo campo de texto chega como string, nunca nulo: ausência é `''`. É o que
 * permite o formulário atribuir o valor direto ao input sem um `?? ''` por
 * linha.
 *
 * `cnpj`, `telefone`, `whatsapp`, `cep` e `cnae` chegam CRUS, só com dígitos.
 * A máscara é da tela (ver `plataforma/mascaras.ts`), pelo mesmo motivo de
 * dinheiro chegar em centavos.
 *
 * E não existe `id` de loja nem de filial: a identidade que atravessa a
 * fronteira é o código legível — CONSTITUICAO.md §2.
 */
import { api } from './api'

/** Matriz ou filial — é a unidade que a NF-e identifica. */
export type TipoUnidade = 'matriz' | 'filial'

export type Filial = {
  /** `001` — o código que o balcão dita. */
  codigo: string
  nome: string
  principal: boolean
}

export type Logo = {
  /**
   * Muda a cada envio, e entra no endereço da imagem.
   *
   * É por isso que trocar a logo não deixa o navegador mostrando a antiga: a
   * logo nova tem outra URL. Ver `urlDaLogo`.
   */
  versao: string
  nome_arquivo: string
  tipo_conteudo: string
  /** Tamanho em bytes — a tela mostra "148 KB" ao lado da miniatura. */
  bytes: number
  atualizada_em: string
}

export type Empresa = {
  // 01 · Identidade
  /** Razão social: o nome jurídico, como está no CNPJ. */
  nome: string
  nome_fantasia: string
  /** 14 dígitos, sem máscara. Imutável — ver `salvarEmpresa`. */
  cnpj: string
  tipo_unidade: TipoUnidade
  tipo_unidade_rotulo: string
  /** Ausente quando a loja nunca enviou uma logo. */
  logo?: Logo

  // 02 · Inscrições fiscais
  inscricao_estadual: string
  inscricao_municipal: string
  /** 7 dígitos da subclasse CNAE (4781-4/00 → `4781400`). */
  cnae: string

  // 03 · Contato e endereço
  telefone: string
  whatsapp: string
  email_publico: string
  cep: string
  logradouro: string
  bairro: string
  cidade: string
  uf: string

  // Plano e unidades: mostrados, não editáveis por esta tela.
  plano: string
  plano_rotulo: string
  status: string
  status_rotulo: string
  avaliacao_expira_em?: string
  dias_restantes: number
  quantidade_filiais: number
  filiais: Filial[]
  criada_em: string
}

/**
 * O corpo do PUT.
 *
 * Não tem `cnpj` nem `quantidade_filiais`: a API os aceita só para RECUSAR
 * com explicação, e a tela não tem por que mandá-los.
 */
export type PedidoEmpresa = {
  nome: string
  nome_fantasia: string
  tipo_unidade: TipoUnidade
  inscricao_estadual: string
  inscricao_municipal: string
  cnae: string
  telefone: string
  whatsapp: string
  email_publico: string
  cep: string
  logradouro: string
  bairro: string
  cidade: string
  uf: string
}

/** Formatos aceitos na logo. Fechado: SVG é documento executável. */
export const TIPOS_DE_LOGO = ['image/png', 'image/jpeg']

/** Teto da logo, o mesmo do CHECK da migração 0014. */
export const MAX_BYTES_LOGO = 2 * 1024 * 1024

/**
 * Endereço da imagem da logo.
 *
 * A versão faz parte do caminho, então a URL é imutável e o navegador pode
 * guardá-la: trocar a logo gera outro endereço em vez de servir outra imagem
 * no mesmo.
 */
export function urlDaLogo(logo: Logo): string {
  return `/api/v1/empresa/logo/${logo.versao}`
}

export function buscarEmpresa(sinal?: AbortSignal): Promise<Empresa> {
  return api.get<Empresa>('/empresa', sinal)
}

export function salvarEmpresa(pedido: PedidoEmpresa): Promise<Empresa> {
  return api.put<Empresa>('/empresa', pedido)
}

/**
 * Envia a logo como binário CRU, com o tipo do próprio arquivo.
 *
 * Cru em vez de multipart: é sempre um arquivo só, e envolvê-lo num FormData
 * obrigaria o servidor a carregar um parser inteiro para extrair a única parte
 * que interessa. Mesmo caminho da foto de produto.
 *
 * Devolve a empresa inteira, e não só a logo, porque é isso que a tela precisa
 * redesenhar.
 */
export function enviarLogo(arquivo: File): Promise<Empresa> {
  const nome = encodeURIComponent(arquivo.name)
  return api.postArquivo<Empresa>(`/empresa/logo?nome=${nome}`, arquivo, arquivo.type)
}

export function removerLogo(): Promise<Empresa> {
  return api.delete<Empresa>('/empresa/logo')
}

/** Monta o corpo do PUT a partir da empresa carregada. */
export function pedidoDe(empresa: Empresa): PedidoEmpresa {
  return {
    nome: empresa.nome,
    nome_fantasia: empresa.nome_fantasia,
    tipo_unidade: empresa.tipo_unidade,
    inscricao_estadual: empresa.inscricao_estadual,
    inscricao_municipal: empresa.inscricao_municipal,
    cnae: empresa.cnae,
    telefone: empresa.telefone,
    whatsapp: empresa.whatsapp,
    email_publico: empresa.email_publico,
    cep: empresa.cep,
    logradouro: empresa.logradouro,
    bairro: empresa.bairro,
    cidade: empresa.cidade,
    uf: empresa.uf,
  }
}
